import React from "react";
import {
  ContactsTable,
  OrganizationsTable,
  BusinessRolesTable,
  AssetsLiabilitiesTable,
  ScheduleOfReoTable,
  AssetsLiabilitiesContactTable,
} from "../ui/LmsSharedTables";
import { addContactFormFields, addMortgageTableFormFields, addAssetsLiabilityFields, handleOnScheduleReoBlur } from "../ui/LmsSharedForms";
import LmsTabPages from "../ui/LmsTabPages";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { useFetchData, useParseRouteActions, isEmptyValue, appendParams } from "../../../helpers/GlobalHelper";
import LmsTable from "../ui/LmsTable";
import { LmsEndpoints } from "../../constants/LmsEndpoints";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { FeatureFlags } from "../../constants/LocalServerConstants";

const BASE_PATH = "/organizations-contacts";
const addContactLicenceFields = (myForm) => {
  myForm.addCenteredGroup([myForm.getDropdownField("Organization Contact", "orgs/list-contacts", { name: "organizationContactId", disabled: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("State", "orgs/list-states", { name: "stateId" })]);
  myForm.addCenteredGroup([myForm.getInputField("License No", { required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Description", { field: "textarea" })]);
  return myForm;
};

const AddContactLicenseForm = ({ contactId }) => {
  const myForm = addContactLicenceFields(new BCFormController(contactId ? { organizationContactId: contactId } : {}, {
    submitSuffix: "orgs/license",
    saveAndContinueSuffix: "organizations-contacts/editlicense/{id}"
  }));

  return <LmsFormGenerator myForm={myForm}/>;
};

const EditContactLicenseForm = ({ id }) => {
  const [{ data, isLoading, error }] = useFetchData(`orgs/license/${id}`);
  const myForm = addContactLicenceFields(new BCFormController(data, {
    submitSuffix: "orgs/update-license",
    deleteSuffix: "orgs/licenses",
    clearSuffixesOnSubmit: [`orgs/license/${id}`]
  }));
  const formActions = ["save", "save-close", "delete", "close"];

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions}/>;
};

export const ContactLicensesTable = ({ suffix = "", contactId = "" }) => {
  const actionsButtons = [
    { text: "Add", path: `/organizations-contacts/addlicense?contactId=${contactId}` },
    { text: "Edit", path: "organizations-contacts/editlicense" },
    "remove",
    "export",
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    deleteSuffix="orgs/licenses"
    sortable={["Created On"]}
    onClickPath={"organizations-contacts/editlicense"}
  />;
};

export const LoanDetailsTable = ({ suffix = "" }) => {
  return <LmsTable
    actionButtons={["export"]}
    apiSuffix={suffix}
    onClickPath="loandetails"
  />;
};

const getUpdatedInputs = (data, inputs) => {
  const addressFields = GlobalConstants.AddressFields;
  addressFields.forEach((field) => {
    if (data && data[field]) {
      inputs[field] = data[field];
    }
  });

  return inputs;
};

const onContactFormChanged = async (options, lastUpdated, fetchData, isInit = false) => {
  if (lastUpdated === "organizationId" || isInit) {
    const { data } = await fetchData(`orgs/info/${options.inputs.organizationId}`).catch(e => console.error(e));
    const newInputs = getUpdatedInputs(data, { ...options.inputs });
    if (data && data?.organizationTypeId) {
      newInputs.organizationTypeId = data?.organizationTypeId;
    }

    options.setInputs(newInputs);
  }
};

export const AddContactForm = ({ orgId = "", setIsModalOpen = undefined as any, dropdownOptions = undefined as any }) => {
  const [{ fetchData }] = useFetchData("", { skipCache: true});
  const [{ data: addressInfo }] = useFetchData(orgId ? `orgs/info/${orgId}` : "");
  const addressInputs = addressInfo ? getUpdatedInputs(addressInfo, {}) : {};
  const initData = orgId ? { organizationId: orgId } : {};
  const onSubmitSuccess = (responseData) => {
    if (FeatureFlags.IS_DEBUG && dropdownOptions) {
      console.log(responseData, dropdownOptions); // TODO: When Contact creation available, add logic to update the dropdown field
    }
  };

  const myForm = new BCFormController({ ...initData, ...addressInfo, ...addressInputs }, { submitSuffix: LmsEndpoints.Orgs.POST.CONTACT_ORG, onSubmitSuccess, excludeFromSubmit: ["organizationTypeId"] });
  myForm.addCenteredGroup([myForm.getDropdownField("Organization", "/orgs/list-user-orgs", { required: true, name: "organizationId", dropdownSkipCache: true })]);
  addContactFormFields(myForm, true);

  const handleClose = setIsModalOpen ? () => setIsModalOpen(false) : undefined;
  const actions = setIsModalOpen ? ["save-continue", "save-close", "close"] : ["save-new", "save-continue", "save-close", "close"];

  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={actions}
      handleClose={handleClose}
      onFormChanged={(options, lastUpdated) => onContactFormChanged(options, lastUpdated, fetchData)}
      onInitLoad={(options) => onContactFormChanged(options, "", fetchData, !!orgId)}
    />
  );
};

const EditContactForm = ({ contactId }) => {
  const updateContactSuffix = ""; // TODO: PUT - Update contact endpoint is on hold at the moment
  const path = "organizations-contacts";
  const [{ fetchData }] = useFetchData();
  const [{ data, isLoading, error }] = useFetchData(`orgs/contact/${contactId}`);
  const values = data?.organizationTypeIds?.includes(GlobalConstants.OrganizationTypes["Loan Company"]) ? { organizationTypeId: GlobalConstants.OrganizationTypes["Loan Company"] } : undefined;
  const myForm = new BCFormController(data, {
    values: { ...values, isLinkedToUserAccount: !!data?.linkedUserAccountDisplay },
    submitSuffix: updateContactSuffix,
    deleteSuffix: "orgs/contacts",
    excludeFromSubmit: ["organizationTypeId", "isLinkedToUserAccount"]
  });

  addContactFormFields(myForm, false, data, contactId);
  const viewAdditionalInfoAction = { text: "View/Edit Additional Information", path: `/organizations-contacts/viewadditionalinfo/${contactId}?agiId=${data?.additionalGuarantorInfoId || ""}` };
  let tabPages = {};

  if (data) {
    if (data.organizationTypeIds && data?.organizationTypeIds.includes(GlobalConstants.OrganizationTypes.Brokerage)) {
      tabPages = {
        Organizations: <OrganizationsTable suffix={`orgs/contact-orgs?contactId=${contactId}`} path={`/${path}/addorganization/${contactId}`} skipClick={true}/>,
        "Business Roles": <BusinessRolesTable suffix={`orgs/contact-business-roles/${contactId}`} path={`/${path}/addbusinessroles/${contactId}`} skipClick={true}/>,
        "Assets/Liabilities": <AssetsLiabilitiesContactTable suffix={`orgs/contact-al-schedule/${contactId}`} contactId={contactId}/>,
        "Schedule of REO": <ScheduleOfReoTable suffix={`orgs/contact-reo-schedule/${contactId}`} path={path} orgId={contactId}/>,
        "Licenses": <ContactLicensesTable suffix={`orgs/contact-licenses/${contactId}`} contactId={contactId}/>,
        "Loan Details": <LoanDetailsTable suffix={`orgs/contact-loan-details/${contactId}`} />,
      };
    }
    else {
      tabPages = {
        Organizations: <OrganizationsTable suffix={`orgs/contact-orgs?contactId=${contactId}`} path={`/${path}/addorganization/${contactId}`} skipClick={true}/>,
        "Business Roles": <BusinessRolesTable suffix={`orgs/contact-business-roles/${contactId}`} path={`/${path}/addbusinessroles/${contactId}`} skipClick={true}/>,
        "Assets/Liabilities": <AssetsLiabilitiesContactTable suffix={`orgs/contact-al-schedule/${contactId}`} contactId={contactId}/>,
        "Schedule of REO": <ScheduleOfReoTable suffix={`orgs/contact-reo-schedule/${contactId}`} path={path} orgId={contactId}/>,
        "Loan Details": <LoanDetailsTable suffix={`orgs/contact-loan-details/${contactId}`} />,
      };
    }
  }

  const editActions = ["save", "save-close", "delete", "close", viewAdditionalInfoAction];

  return <React.Fragment>
    <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={editActions} onFormChanged={(options, lastUpdated) => onContactFormChanged(options, lastUpdated, fetchData)}/>
    <LmsTabPages tabPages={tabPages}/>
  </React.Fragment>;
};

const addContactOrganizationFields = (myForm) => {
  myForm.addCenteredGroup([
    myForm.getDropdownField("Organization Contact", "orgs/list-contacts", {
      disabled: true,
      name: "organizationContactId",
      required: true,
    }),
  ]);

  myForm.addCenteredGroup([
    myForm.getDropdownField("Organization", "orgs/list-orgs", {
      required: true,
      dropdownSkipCache: true,
    }),
  ]);

  myForm.addCenteredGroup([myForm.getInputField("Description", { field: "textarea" })]);
  return myForm;
};

const AddContactOrganizationForm = ({ contactId }) => {
  const myForm = addContactOrganizationFields(new BCFormController(
    { organizationContactId: contactId },
    {
      submitSuffix: "orgs/contact-orgs",
      saveAndContinueSuffix: "organizations-contacts/editorganization/{id}",
    }
  ));

  return <LmsFormGenerator myForm={myForm} />;
};

const EditContactOrganizationForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`orgs/contact-orgs/${id}`);
  const myForm = addContactOrganizationFields(new BCFormController(
    { id, ...data },
    {
      submitSuffix: "orgs/contact-orgs",
      deleteSuffix: "orgs/contact-orgs",
    }
  ));

  return (
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      error={error}
    />
  );
};

export const ViewAdditionalInfoForm = ({ contactId, additionalGuarantorInfoId = "", handleClose = undefined as any }) => {
  const [{ data, isLoading, error, fetchData }, callApiNow] = useFetchData(contactId? `orgs/contact-additional-guarantor-info/${contactId}` : "", { defaultData: {} });
  const noRealEstate = !!data?.noRealEstate;
  const noAssets = !!data?.noAsset;
  const assetOrganizationContactId = data?.assetOrganizationContactId || "";
  const params = [{ id: additionalGuarantorInfoId }, { noRealEstate }, { noAssets }, { assetContactId: assetOrganizationContactId }];
  const getAdditionalInfoSuffix = appendParams( "orgs/additional-guarantor-info", params);
  const [{ data: additionalInfo, isLoading: additionalInfoLoading }] = useFetchData(data ? getAdditionalInfoSuffix : "");
  const myForm = new BCFormController({ ...data, ...additionalInfo }, {
    submitSuffix: LmsEndpoints.Orgs.PUT.ADDITIONAL_GUARANTOR_INFO,
    validations: !data || data?.uscitizen ? {} : { citizenshipStatus: ["required"] },
    onSubmitSuccess: callApiNow
  });

   myForm.addGroupFields([
     myForm.getDropdownField("Organization Contact", "orgs/list-contacts", { disabled: true, value: contactId, name: "organizationContactId" }),
     myForm.getSocialSecurityField("Social Security Number", data?.socialSecurityNumber),
     myForm.getCalendarField("Date Of Birth"),
     myForm.getDropdownField("Marital Status", "metadata/list-marital-status", { name: "maritalStatusId" }),
     myForm.getInputField("Spouse Name"),
   ]);
   myForm.addGroupFields([
     myForm.getInputField("Employer Name"),
     myForm.getInputField("Position", { name: "employerPosition" }),
     myForm.getNumberField("Years In Industry"),
     myForm.getNumberField("Years In Job"),
     myForm.getCheckboxField("Self Employed"),
   ]);
   myForm.addGroupFields([
     myForm.getCurrencyField("Gross Revenue (Two Year Average)", { name: "monthlyRevenue", required: true, isHidden: { selfEmployed: true }, isHiddenOperation: "notequal" , wrapLabel: true }),
     myForm.getCurrencyField("Net Income (Two Year Average)", { required: true, name: "monthlyIncome", wrapLabel: true }),
     myForm.getCheckboxField("No Real Estate Owned", { name: "noRealEstate", wrapLabel: true }),
     myForm.getCheckboxField("No Assets/Liabilities", { name: "noAsset", wrapLabel: true }),
     myForm.getBlankField(),
   ], { widths: 4 });
  myForm.addGroupFields([
    myForm.getCurrencyField("Value Of Real Estate Owned", {
      actionPath: `/organizations-contacts/viewscheduleofreo/${contactId}`,
      actionLabel: "View Schedule Of REO",
      disabled: true,
      isHidden: { noRealEstate: true }
    }),
    myForm.getCurrencyField("Value Of Real Estate Owned", { isHidden: { noRealEstate: true }, isHiddenOperation: "notequal", disabled: true }),
    myForm.getCurrencyField("Value Of Stocks And Bonds", {
      actionPath: `/organizations-contacts/viewassetsliabilities/${contactId}`,
      actionLabel: "View Assets/Liabilities",
      disabled: true,
      isHidden: { noAsset: true }
    }),
    myForm.getCurrencyField("Value Of Stocks And Bonds", { isHidden: { noAsset: true }, isHiddenOperation: "notequal", disabled: true }),
    myForm.getBlankField({ isHidden: { selfEmployed: true } }),
  ]);
   myForm.addGroupFields([
     myForm.getNumberField("# Of Properties Owned", { name: "numberOfPropertiesOwned", disabled: true }),
     myForm.getCurrencyField("Cash In Bank", { disabled: true }),
     myForm.getNumberField("Credit Score"),
     myForm.getCurrencyField("Value Of Retirement Fund", { disabled: true }),
     myForm.getCalendarField("Credit Score Updated On"),
   ]);
   myForm.addGroupFields([
     myForm.getCurrencyField("Total Liabilities", { disabled: true }),
     myForm.getCurrencyField("Net Worth", { disabled: true, isHidden: { selfEmployed: true } }),
     myForm.getNumberField("Estimated Credit Score"),
     myForm.getCheckboxField("Are You A U.S. Citizen?", { name: "uscitizen" }),
     myForm.getInputField("Status", { name: "citizenshipStatus" }),
     myForm.getBlankField({ isHidden: { selfEmployed: true }, isHiddenOperation: "notequal" }),
   ]);
   myForm.addGroupFields([
     myForm.getLabelField("Any Pending or Threatened Legal Actions?", { position: "right-center", width: 4, wrapLabel: true }),
     myForm.getCheckboxField("", { name: "pendingLegalAction" }),
     myForm.getInputField("", { name: "legalActionDescription", width: 8 }),
     myForm.getBlankField(),
     myForm.getBlankField(),
     myForm.getBlankField(),
   ], { widths: 1 });
   myForm.addGroupFields([
     myForm.getLabelField("Foreclosure Ever Initiated Against You?", { position: "right-center", width: 4, wrapLabel: true }),
     myForm.getCheckboxField("", { name: "isForeclosureInitiated" }),
     myForm.getInputField("", { name: "foreclosureDescription", width: 8 }),
     myForm.getBlankField(),
     myForm.getBlankField(),
     myForm.getBlankField(),
   ], { widths: 1 });
   myForm.addGroupFields([
     myForm.getLabelField("Other Names Under Which You Have Been Granted Credit", { position: "right-center", width: 4, wrapLabel: true }),
     myForm.getInputField("", { name: "otherCreditName", width: 4 }),
   ], { widths: 1 });
   myForm.addGroupFields([
     myForm.getLabelField("Comments", { position: "right-center", width: 4 }),
     myForm.getTextareaField("", { name: "comment", width: 8 }),
   ], { widths: 1 });

  const onFormChanged = async (options, lastUpdated) => {
    const noRealEstateInput = !!options?.inputs?.noRealEstate;
    const noAssetsInput = !!options?.inputs?.noAsset;
    if (lastUpdated === "uscitizen") {
      options.setValidations({ ...myForm.validations, citizenshipStatus: !options?.inputs?.uscitizen ? ["required"] : [] });
    }

    if (lastUpdated === "noRealEstate" || lastUpdated === "noAsset") {
      const params = [{ id: additionalGuarantorInfoId }, { noRealEstate: noRealEstateInput }, { noAssets: noAssetsInput }, { assetContactId: assetOrganizationContactId }];
      const newDetails = await fetchData(appendParams( "orgs/additional-guarantor-info", params));
      if (newDetails?.data) {
        options.setInputs({ ...options?.inputs, ...newDetails?.data });
      }
    }

    if (lastUpdated === "creditScore" && !isEmptyValue(options?.inputs?.creditScore)) {
      if (options?.inputs?.creditScore > 900) {
        options.setInputs({ ...options?.inputs, creditScore: 900 });
      }
    }
  };

  const handleOnBlur = (name, value, options) => {
    if (name === "creditScore" && !isEmptyValue(value) && value < 100) {
      if (value < 100) {
        options.setInputs({ ...options?.inputs, creditScore: 100 });
      }
    }
  };

  const refreshButton = { text: "Refresh", handleClick: callApiNow };
  const actions = ["save", "save-close", refreshButton, "close"];
  return <LmsFormGenerator
    myForm={myForm}
    isLoading={isLoading || additionalInfoLoading}
    error={error}
    actions={actions}
    onFormChanged={onFormChanged}
    onBlur={handleOnBlur}
    handleClose={handleClose}
  />;
};

const AddAssetsLiabilitiesForm = ({ contactId }) => {
  const myForm = new BCFormController({ organizationContactId: contactId }, { submitSuffix: "orgs/al-schedule" });
  myForm.addCenteredGroup([myForm.getDropdownField("Organization Contact", contactId ? `orgs/list-contacts/${contactId}` : "", { disabled: true })]);
  addAssetsLiabilityFields(myForm);

  return <LmsFormGenerator myForm={myForm}/>;
};

const EditAssetsLiabilitiesForm = ({ id }) => {
  const getAlSchedule = `orgs/al-schedule/${id}`;
  const [{ data = {}, isLoading, error }] = useFetchData(getAlSchedule);
  const myForm = new BCFormController(data, { submitSuffix: "orgs/update-al-schedule", deleteSuffix: "orgs/al-schedules", clearSuffixesOnSubmit: [getAlSchedule] });
  myForm.addCenteredGroup([myForm.getDropdownField("Organization Contact", "orgs/list-contacts", { disabled: true })]);
  addAssetsLiabilityFields(myForm);
  const formActions = ["save", "save-close", "delete", "close"];

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions}/>;
};

const addContactScheduleOfReoFields = (myForm) => {
  const marketValueValidation = ({ inputs }) => {
    const { marketValue } = inputs;
    if (isEmptyValue(marketValue) || marketValue < 1) {
      return { message: "value greater than or equal to 1" };
    }

    return false;
  };

  myForm.addGroupFields([
    myForm.getDropdownField("Organization Contact", "orgs/list-contacts", { disabled: true, name: "organizationContactId" }),
    myForm.getDropdownField("REO Status", "orgs/list-reo-statuses", { name: "reostatusId", required: true }),
    myForm.getDropdownField("REO Type", "orgs/list-reo-types", { name: "reotypeId", required: true }),
    myForm.getBlankField()
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Market Value", { required: true, errorLabel: "Please enter", validations: [{ custom: marketValueValidation }] }),
    myForm.getCurrencyField("Gross Rental Income", { name: "grossRentalIncome", required: true }),
    myForm.getInputField("% Owned", { name: "percentageOwned", fieldType: "percent" }),
    myForm.getBlankField()
  ]);

  myForm.addGroupFields([
    myForm.getZipCodeField(),
    myForm.getStateField(),
    myForm.getCityField(),
    myForm.getStreetField(),
  ]);
  return addMortgageTableFormFields(myForm);
};

const AddContactScheduleOfReoForm = ({ contactId }) => {
  const myForm = addContactScheduleOfReoFields(
    new BCFormController(
      {
        organizationContactId: contactId,
        mortgageAmount1: 0,
        mortgageAmount2: 0,
        mortgageAmount3: 0,
        mortgagePayment1: 0,
        mortgagePayment2: 0,
        mortgagePayment3: 0
      },
      {
        skipPlaceHolder: true,
        submitSuffix: LmsEndpoints.Orgs.POST.REO_SCHEDULE,
        saveAndContinueSuffix: `${BASE_PATH}/editscheduleofreo/{id}`,
      }
    )
  );

  return <LmsFormGenerator myForm={myForm} onBlur={handleOnScheduleReoBlur} />;
};

const EditContactScheduleOfReoForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`orgs/reo-schedule/${id}`);
  const myForm = addContactScheduleOfReoFields(new BCFormController(data, {
    submitSuffix: "orgs/update-reo-schedule",
    deleteSuffix: "orgs/reo-schedules",
    clearSuffixesOnSubmit: [`orgs/reo-schedule/${id}`]
  }));

  const formActions = ["save", "save-close", "delete", "close"];
  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions} onBlur={handleOnScheduleReoBlur} />;
};

export const AddBusinessRolesTable = ({ contactId }) => {
  const actionsButtons = [
    "addSelected",
    "close"
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={`orgs/business-roles/${contactId}`}
    addSelectedSuffix={`orgs/contact-business-roles?organizatinContactId=${contactId}`}
    clearSuffixesOnSuccess={[`orgs/contact-business-roles/${contactId}`]}
    sortable={["Created On"]}
  />;
};

const LmsOrganizationsContacts = ({ ...props }) => {
  const { params = {}, history } = props;
  const allowedActions = ["add"];
  const alSubPages = ["addassestliability", "editassestliability", "viewassetsliabilities"];
  const reoPages = ["addscheduleofreo", "editscheduleofreo", "viewscheduleofreo"];
  const licencesPages = ["addlicense", "editlicense"];
  const allowedSubpages = ["addorganization", "editorganization", "addbusinessroles", "viewadditionalinfo", ...alSubPages, ...reoPages, ...licencesPages] as any[];
  const { isAdd, isEdit, actionLower, isMainPage, isSubpageAllowed, isActionAllowed, id, query } = useParseRouteActions(params, allowedActions, allowedSubpages);
  const contactId = query.get("contactId") || id;
  const orgId = query.get("orgId") || id;
  const agiId = query.get("agiId") || "";
  const path = "organizations-contacts";

  if (!isActionAllowed) {
    history.goBack();
  }

  return <React.Fragment>
    {isMainPage && <ContactsTable />}
    {isAdd && <AddContactForm orgId={orgId} />}
    {isEdit && <EditContactForm contactId={contactId} />}
    {isSubpageAllowed && <React.Fragment>
      {actionLower === "addorganization" && <AddContactOrganizationForm contactId={contactId} />}
      {actionLower === "editorganization" && <EditContactOrganizationForm id={id} />}
      {actionLower === "addbusinessroles" && <AddBusinessRolesTable contactId={contactId} />}
      {actionLower === "addassestliability" && <AddAssetsLiabilitiesForm contactId={contactId} />}
      {actionLower === "editassestliability" && <EditAssetsLiabilitiesForm id={id} />}
      {actionLower === "addscheduleofreo" && <AddContactScheduleOfReoForm contactId={contactId} />}
      {actionLower === "editscheduleofreo" && <EditContactScheduleOfReoForm id={id} />}
      {actionLower === "viewscheduleofreo" && <ScheduleOfReoTable suffix={`orgs/contact-reo-schedule/${contactId}`} path={path} orgId={orgId} />}
      {actionLower === "addlicense" && <AddContactLicenseForm contactId={contactId} />}
      {actionLower === "editlicense" && <EditContactLicenseForm id={id} />}
      {actionLower === "viewassetsliabilities" && <AssetsLiabilitiesTable path={path} orgId={id} />}
      {actionLower === "viewadditionalinfo" && <React.Fragment>
        <ViewAdditionalInfoForm contactId={contactId} additionalGuarantorInfoId={agiId} />
        <LmsTable actionButtons={["export"]} apiSuffix={`orgs/credit-requests/${contactId}`} title="Credit Requests" />
      </React.Fragment>}
    </React.Fragment>}
  </React.Fragment>;
};

export default LmsOrganizationsContacts;